import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Ensure environment variables are defined
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced validation of Supabase configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase configuration:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey,
    timestamp: new Date().toISOString()
  });
  throw new Error(
    'Missing Supabase configuration. Please:\n' +
    '1. Click the "Connect to Supabase" button in the top right\n' +
    '2. Follow the setup process to connect your Supabase project\n' +
    '3. Ensure the environment variables are properly set in .env'
  );
}

// Validate URL format before creating client
try {
  new URL(supabaseUrl);
} catch (e) {
  console.error('Invalid Supabase URL format:', {
    url: supabaseUrl,
    error: e instanceof Error ? e.message : 'Unknown error'
  });
  throw new Error(`Invalid Supabase URL format: ${supabaseUrl}`);
}

console.log('Initializing Supabase client', {
  url: supabaseUrl,
  timestamp: new Date().toISOString()
});

// Configure client with enhanced error handling and timeouts
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-client-info': 'legislative-clarity@1.0.0',
    },
    fetch: (url, options = {}) => {
      // Add timeout and better error handling to fetch requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      return fetch(url, {
        ...options,
        signal: controller.signal,
      }).finally(() => {
        clearTimeout(timeoutId);
      }).catch(error => {
        console.error('Supabase fetch error:', {
          url,
          error: error.message,
          name: error.name,
          timestamp: new Date().toISOString()
        });
        
        // Provide more specific error messages
        if (error.name === 'AbortError') {
          throw new Error('Request timed out. Please check your internet connection and try again.');
        } else if (error.message.includes('Failed to fetch')) {
          throw new Error('Unable to connect to database. Please check:\n1. Your internet connection\n2. Supabase project status\n3. CORS settings in Supabase dashboard');
        }
        
        throw error;
      });
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 2
    }
  }
});

// Export a function to diagnose connection issues
export const diagnoseConnection = async (): Promise<{
  hasConfig: boolean;
  canReachServer: boolean;
  canQueryDatabase: boolean;
  error?: string;
}> => {
  const result = {
    hasConfig: false,
    canReachServer: false,
    canQueryDatabase: false,
    error: undefined as string | undefined
  };

  try {
    // Check configuration
    result.hasConfig = !!(supabaseUrl && supabaseAnonKey);
    if (!result.hasConfig) {
      result.error = 'Missing Supabase configuration';
      return result;
    }

    // A real table query is the connectivity check. Supabase no longer allows
    // anon keys to access /rest/v1/ (the OpenAPI schema endpoint), so probing
    // that URL creates a false outage while normal Data API queries still work.
    try {
      const { error } = await supabase.from('bills').select('id').limit(1);
      result.canReachServer = true;
      result.canQueryDatabase = !error;
      if (error) {
        result.error = `Database query failed: ${error.message}`;
      }
    } catch (e) {
      result.canReachServer = false;
      result.error = `Database query error: ${e instanceof Error ? e.message : 'Unknown error'}`;
    }

    return result;
  } catch (e) {
    result.error = `Diagnosis failed: ${e instanceof Error ? e.message : 'Unknown error'}`;
    return result;
  }
};
